import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  CheckCircle2,
  Loader2,
  User,
  Sparkles,
  Lightbulb,
  Clock,
  AlertCircle,
  Microscope,
  FlaskConical,
  FileText,
  Pill,
  ClipboardList,
  Activity,
  Apple,
  Dumbbell,
  UtensilsCrossed,
  Brain,
  Scale,
  Stethoscope,
  Heart,
  RefreshCw,
  X,
  ChevronLeft
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import {
  getSmartfileSession,
  generateSmartfileInsights,
  updateSmartfileProfessionalNotes,
  finalizeSmartfileSession,
  extractPatientInfoFromText,
  linkPatientToSmartfileSession,
  fuzzySearchPatients,
  SmartfileSession,
} from "@/services/smartfile";
import { isAIDisabled } from "@/lib/smartfile-ai-preference";
import { supabase } from "@/integrations/supabase/client";
import { PatientLinker } from "@/components/smartfile/PatientLinker";
import { ExtractedContentEditor } from "@/components/smartfile/ExtractedContentEditor";
import { GroupedRecordsView } from "@/components/smartfile/GroupedRecordsView";
import { GroupedResultsView } from "@/components/smartfile/GroupedResultsView";
import { SmartfilePhysiotherapyEvolutionDetails } from "@/components/patient/SmartfilePhysiotherapyEvolutionDetails";
import { SmartfileAnthropometryDetails } from "@/components/patient/SmartfileAnthropometryDetails";
import { NutritionalAssessmentCard } from "@/components/smartfile/NutritionalAssessmentCard";
import { SmartfileTrainingPlanDetails } from "@/components/patient/SmartfileTrainingPlanDetails";
import { SmartfileSessionDetails } from "@/components/patient/SmartfileSessionDetails";
import { VitalsCard } from "@/components/smartfile/VitalsCard";
import { SmartfileVitalsDetails } from "@/components/patient/SmartfileVitalsDetails";
import { SmartfileMealPlanDetails } from "@/components/patient/SmartfileMealPlanDetails";
import { SmartfileMealPlanFoodDetails } from "@/components/patient/SmartfileMealPlanFoodDetails";
import { PsychotherapySessionCard } from "@/components/smartfile/PsychotherapySessionCard";
import { ChiropracticAdjustmentCard } from "@/components/smartfile/ChiropracticAdjustmentCard";
import { AestheticProtocolCard } from "@/components/smartfile/AestheticProtocolCard";
import { BodyCompositionCard } from "@/components/smartfile/BodyCompositionCard";
import { SmartfileAnamnesisDetails } from "@/components/patient/SmartfileAnamnesisDetails";
import { SmartfileMedicalReportDetails } from "@/components/patient/SmartfileMedicalReportDetails";
import { SmartfileBiopsyDetails } from "@/components/patient/SmartfileBiopsyDetails";
import { useMobileNav } from "@/contexts/MobileNavContext";
import { useLinkByName } from "@/hooks/useLinkByName";
import { CategorizationSuggestions } from "@/components/smartfile/CategorizationSuggestions";
import { useQueryClient } from "@tanstack/react-query";
import { validateCPF, generateValidCPF } from "@/lib/cpf-validator";


interface Step2AnalyzeProps {
  sessionId: string;
  onComplete: () => void | Promise<void>;
  onBack?: () => void;
  fromAssisted?: boolean;
  hideDiscard?: boolean;
  isOnboarding?: boolean;
}

export default function Step2Analyze({ sessionId, onComplete, onBack, fromAssisted, hideDiscard = false, isOnboarding = false }: Step2AnalyzeProps) {
  const navigate = useNavigate();
  const { setShowMobileNav } = useMobileNav();
  const linkByName = useLinkByName();
  const queryClient = useQueryClient();
  const [session, setSession] = useState<SmartfileSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [finalizing, setFinalizing] = useState(false);
  const [professionalNotes, setProfessionalNotes] = useState("");
  const [patientInfo, setPatientInfo] = useState<any>(null);
  const [extractedPatientInfo, setExtractedPatientInfo] = useState<{
    name: string | null;
    cpf: string | null;
    confidence: number;
  } | null>(null);
  const [editableRecords, setEditableRecords] = useState<any[]>([]);
  const [processingStage, setProcessingStage] = useState<string>("");
  const [processingProgress, setProcessingProgress] = useState<number>(0);
  
  // ✅ Estados para campos editáveis no onboarding
  const [editablePatientName, setEditablePatientName] = useState<string>("");
  const [editablePatientCPF, setEditablePatientCPF] = useState<string>("");

  // ✅ Debounce ref para prevenir chamadas duplicadas
  const lastGenerationAttemptRef = useRef<number>(0);
  const generationInProgressRef = useRef<boolean>(false);
  const backgroundMonitorRef = useRef<any>(null);

  useEffect(() => {
    setShowMobileNav(false);
    return () => {
      setShowMobileNav(true);
      // Limpar monitoramento em background ao desmontar
      if (backgroundMonitorRef.current) {
        supabase.removeChannel(backgroundMonitorRef.current);
        backgroundMonitorRef.current = null;
      }
    };
  }, [setShowMobileNav]);

  useEffect(() => {
    loadSession();
  }, [sessionId]);

  // ✅ ONBOARDING: Inicializar campos editáveis quando dados extraídos estiverem disponíveis
  useEffect(() => {
    if (isOnboarding && (extractedPatientInfo || session?.temp_patient_data)) {
      const name = extractedPatientInfo?.name || session?.temp_patient_data?.name || "";
      const cpf = extractedPatientInfo?.cpf || session?.temp_patient_data?.cpf || "";
      
      // Só inicializar se os campos estiverem vazios e houver dados para preencher
      if ((name || cpf) && (!editablePatientName && !editablePatientCPF)) {
        setEditablePatientName(name);
        setEditablePatientCPF(cpf);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboarding, extractedPatientInfo?.name, extractedPatientInfo?.cpf, session?.temp_patient_data?.name, session?.temp_patient_data?.cpf]);

  // ✅ ONBOARDING: Inicializar campos editáveis quando paciente já estiver vinculado
  useEffect(() => {
    if (isOnboarding && patientInfo) {
      // Sempre atualizar os campos editáveis com os dados do paciente vinculado
      setEditablePatientName(patientInfo.full_name || "");
      setEditablePatientCPF(patientInfo.cpf || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboarding, patientInfo?.id, patientInfo?.full_name, patientInfo?.cpf]);

  // Processar arquivo quando vier de coleta assistida
  useEffect(() => {
    if (fromAssisted && session && !session.extracted_text) {
      processAssistedSubmission();
    }
  }, [fromAssisted, session]);

  // Listener para recarregar quando o tipo de documento mudar no header
  useEffect(() => {
    const handleReload = () => {
      loadSession();
    };

    window.addEventListener('smartfile-reload', handleReload);
    return () => window.removeEventListener('smartfile-reload', handleReload);
  }, [sessionId]);

  // Helper para buscar paciente por CPF ou nome usando fuzzy search
  const findMatchingPatient = async (
    name: string | null,
    cpf: string | null
  ): Promise<string | null> => {
    if (!name && !cpf) return null;

    // Usar busca fuzzy com threshold conservador (60%)
    const results = await fuzzySearchPatients(
      name || '',
      cpf,
      0.6 // só auto-vincular se similaridade >= 60%
    );

    if (results.length === 0) return null;

    const bestMatch = results[0];

    // Auto-vincular apenas se:
    // 1. CPF match exato, OU
    // 2. Similaridade >= 85%
    if (
      bestMatch.matchType === 'exact_cpf' ||
      (bestMatch.similarity && bestMatch.similarity >= 0.85)
    ) {
      console.log(`Auto-vinculado: ${bestMatch.full_name} (${(bestMatch.similarity || 0) * 100}%)`);
      return bestMatch.id;
    }

    // Se similaridade < 85%, mostrar PatientLinker com sugestões
    return null;
  };

  const processAssistedSubmission = async () => {
    if (!session) return;

    try {
      setProcessingStage("Preparando arquivo...");
      setProcessingProgress(5);

      const { data: fileData, error: downloadError } = await supabase.storage
        .from('patient-docs')
        .download(session.file_path);

      if (downloadError) throw downloadError;

      setProcessingStage("Arquivo baixado");
      setProcessingProgress(15);

      // Buscar metadados da sessão para determinar tipo
      const { data: sessionMeta } = await supabase
        .from('smartfile_sessions')
        .select('file_type, file_name')
        .eq('id', session.id)
        .single();

      // Detectar tipo por extensão se file_type estiver incorreto
      let fileType = sessionMeta?.file_type || 'application/octet-stream';

      if (fileType === 'application/octet-stream' && sessionMeta?.file_name) {
        const ext = sessionMeta.file_name.split('.').pop()?.toLowerCase();

        if (ext === 'jpg' || ext === 'jpeg') fileType = 'image/jpeg';
        else if (ext === 'png') fileType = 'image/png';
        else if (ext === 'webm') fileType = 'audio/webm';
        else if (ext === 'mp3') fileType = 'audio/mp3';
        else if (ext === 'wav') fileType = 'audio/wav';
        else if (ext === 'pdf') fileType = 'application/pdf';
        else if (ext === 'xlsx' || ext === 'xls') fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

        console.log(`[Step2Analyze] ⚡ Tipo detectado por extensão: ${fileType}`);
      }

      const file = new File(
        [fileData as Blob],
        sessionMeta?.file_name || 'document',
        { type: fileType }
      );

      setProcessingStage("Iniciando análise...");
      setProcessingProgress(20);

      const { processFileUploadUltraFast } = await import("@/services/smartfile-fast");
      const { markAssistedCollectionOrigin } = await import("@/services/smartfile-assisted");

      await processFileUploadUltraFast(
        session.id,
        file,
        (stage, progress) => {
          setProcessingStage(stage);
          setProcessingProgress(progress);
        },
        { skipUpload: true }
      );

      // Buscar source_submission_id das assisted_submissions
      const { data: submission } = await supabase
        .from('assisted_submissions')
        .select('id')
        .eq('smartfile_session_id', session.id)
        .single();

      if (submission) {
        await markAssistedCollectionOrigin(session.id, submission.id);
      }

      setProcessingStage("Concluído!");
      setProcessingProgress(100);

      toast.success("✅ Análise concluída com sucesso!");

      await loadSession();

      // Limpar estado de processamento após loadSession para exibir conteúdo
      setTimeout(() => {
        setProcessingStage("");
        setProcessingProgress(0);
      }, 1500);
    } catch (error: any) {
      console.error('[processAssistedSubmission] Error:', error);
      toast.error(error.message || "Erro ao processar arquivo");
    }
  };

  const loadSession = async () => {
    try {
      const data = await getSmartfileSession(sessionId);
      setSession(data);
      setProfessionalNotes(data.professional_notes || "");

      // ✅ Carregar registros estruturados editáveis
      // Sempre usar dados do banco (que já incluem edições salvas automaticamente)
      if (data.structured_records) {
        const recordsFromDb = Array.isArray(data.structured_records) ? data.structured_records : [];
        console.log(`[Step2] 📋 Loaded ${recordsFromDb.length} structured records from DB`);
        console.log(`[Step2] Content Type: ${data.content_type}`);

        // Validação específica para meal_plan
        if (data.content_type === 'meal_plan') {
          console.log(`[Step2] 🍽️ MEAL_PLAN DEBUG:`);
          console.log(`[Step2] All records:`, recordsFromDb);
          const mealPlanRecords = recordsFromDb.filter(r => r.type === 'meal_plan');
          console.log(`[Step2] Meal Plan Records Count:`, mealPlanRecords.length);

          if (mealPlanRecords.length === 0) {
            console.warn(`[Step2] ⚠️ No meal_plan records found!`);
            console.warn(`[Step2] Record types found:`, recordsFromDb.map(r => ({
              type: r.type,
              record_type: r.record_type,
              keys: Object.keys(r)
            })));
          } else {
            mealPlanRecords.forEach((record, idx) => {
              console.log(`[Step2] Meal Plan Record ${idx}:`, {
                type: record.type,
                plan_name: record.plan_name,
                hasMeals: Array.isArray(record.meals),
                mealsCount: Array.isArray(record.meals) ? record.meals.length : 0
              });
            });
          }
        }

        // ✅ Sempre usar dados do banco (que já incluem edições salvas por handleRecordsChange)
        setEditableRecords(recordsFromDb);
      } else {
        console.warn(`[Step2] ⚠️ No structured_records found in session`);
        console.warn(`[Step2] Session data:`, {
          id: data.id,
          content_type: data.content_type,
          status: data.status,
          hasStructuredRecords: !!data.structured_records
        });
        setEditableRecords([]);
      }

      // Log de diagnóstico
      console.log('[Debug Step2]', {
        sessionId,
        extractedLength: data.extracted_text?.length,
        extractionStatus: data.extraction_status,
        contentType: data.content_type,
        structuredCount: Array.isArray(data.structured_records) ? data.structured_records.length : 0,
        hasInsights: !!data.ai_insights,
        fromAssisted,
      });

      if (data.patient_id) {
        const { data: patientData, error: patientError } = await supabase
          .from("patients_onboarding")
          .select("id, full_name, cpf, data_nascimento, sexo, celular")
          .eq("id", data.patient_id)
          .single();

        if (!patientError && patientData) {
          setPatientInfo(patientData);
          
          // ✅ ONBOARDING: Inicializar campos editáveis com dados do paciente vinculado
          if (isOnboarding) {
            setEditablePatientName(patientData.full_name || "");
            setEditablePatientCPF(patientData.cpf || "");
          }
        }
      } else {
        // Tentar vincular automaticamente
        if (data.extracted_text) {
          const patientData = await extractPatientInfoFromText(data.extracted_text);
          setExtractedPatientInfo(patientData);
          
          // ✅ ONBOARDING: Inicializar campos editáveis com dados extraídos
          if (isOnboarding) {
            setEditablePatientName(patientData.name || "");
            setEditablePatientCPF(patientData.cpf || "");
          }

          // Confiança alta (>80%): vincular automaticamente
          if (patientData.confidence > 0.8) {
            const matchedPatientId = await findMatchingPatient(
              patientData.name,
              patientData.cpf
            );

            if (matchedPatientId) {
              toast.loading("Vinculando paciente automaticamente...");
              await linkPatientToSmartfileSession(sessionId, matchedPatientId);
              toast.dismiss();
              toast.success("Paciente vinculado automaticamente!");

              // ✅ loadSession() irá detectar patient_id e chamar generateResultsAutomatically()
              // automaticamente via lógica nas linhas 177-199
              await loadSession(); // Recarregar (já gera os resultados)
              return;
            }
          }
        } else if (data.temp_patient_data) {
          // Armazenar dados temporários extraídos
          setExtractedPatientInfo({
            name: data.temp_patient_data.name,
            cpf: data.temp_patient_data.cpf,
            confidence: 0.5
          });
          
          // ✅ ONBOARDING: Inicializar campos editáveis com dados temporários
          if (isOnboarding) {
            setEditablePatientName(data.temp_patient_data.name || "");
            setEditablePatientCPF(data.temp_patient_data.cpf || "");
          }
        }
      }

      // Query automática carrega os resultados via useQuery

      // Geração automática de insights (apenas se AI não estiver desabilitada)
      if (data.structured_records && !data.ai_insights && !data.ai_summary && !isAIDisabled()) {
        console.log("Gerando insights automaticamente...");
        await generateInsightsAutomatically(data);
      } else if (isAIDisabled()) {
        console.log("⏭️ Insights desabilitados pelo usuário (localStorage)");
      }

      // ✅ REMOVIDO: Não gerar resultados automaticamente - usar apenas dados de structured_records
      // Os dados já foram processados por smartfile-v15-process-with-tools e salvos em structured_records
      // Serão salvos quando o usuário finalizar a sessão via saveStructuredRecords
    } catch (error: any) {
      toast.error("Erro ao carregar sessão");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const generateInsightsAutomatically = async (sessionData: SmartfileSession) => {
    try {
      setGenerating(true);
      await generateSmartfileInsights(
        sessionId,
        sessionData.structured_records,
        sessionData.temp_patient_data
      );
      const updated = await getSmartfileSession(sessionId);
      setSession(updated);
    } catch (error) {
      console.error("Erro ao gerar insights automaticamente:", error);
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateInsights = async () => {
    if (!session?.structured_records) return;
    
    if (isAIDisabled()) {
      toast.info("Resumo e Insights da IA estão desativados. Ative no Dashboard para gerar insights.");
      return;
    }

    setGenerating(true);
    try {
      await generateSmartfileInsights(
        sessionId,
        session.structured_records,
        session.temp_patient_data
      );
      toast.success("Insights gerados!");
      await loadSession();
    } catch (error: any) {
      toast.error("Erro ao gerar insights");
      console.error(error);
    } finally {
      setGenerating(false);
    }
  };

  // ✅ REMOVIDO: handleGenerateResults não é mais necessário
  // Os dados já foram processados por smartfile-v15-process-with-tools
  // Serão salvos quando o usuário finalizar a sessão via saveStructuredRecords
  const handleGenerateResults = async () => {
    toast.info("Os dados já foram processados. Finalize a sessão para salvar no histórico do paciente.");
  };

  const handleSaveNotes = async () => {
    try {
      await updateSmartfileProfessionalNotes(sessionId, professionalNotes);
      toast.success("Notas salvas!");
    } catch (error: any) {
      toast.error("Erro ao salvar notas");
    }
  };

  // ✅ Função para reprocessar documento se extração falhou
  const retryExtraction = async () => {
    if (!session) return;

    try {
      toast.loading("Reprocessando documento...");
      setGenerating(true);

      // Forçar reprocessamento via edge function
      const { data, error } = await supabase.functions.invoke(
        "smartfile-v15-extract-text",
        { body: { filePath: session.file_path, fileType: session.file_type, sessionId } }
      );

      if (error) throw error;

      if (data?.aiModel) {
        console.log(`[Step2Analyze] 🤖 Extraction AI Model: ${data.aiModel.provider}/${data.aiModel.model_code} (${data.aiModel.name || 'N/A'})`);
      }
      console.log(`[Retry] ✅ Text extracted: ${data.text.length} chars`);

      // Reclassificar com o novo texto
      const { data: classifyData, error: classifyError } = await supabase.functions.invoke(
        "smartfile-v15-classify-content-fast",
        { body: { extractedText: data.text, fileType: session.file_type } }
      );

      if (classifyError) throw classifyError;

      console.log(`[Retry] ✅ Classified as: ${classifyData.contentType}`);

      // Estruturar registros
      const { data: structureData, error: structureError } = await supabase.functions.invoke(
        "smartfile-v15-structure-records-fast",
        { body: { extractedText: data.text, contentType: classifyData.contentType } }
      );

      if (structureError) throw structureError;

      console.log(`[Retry] ✅ Structured ${structureData.records?.length || 0} records`);

      // Atualizar sessão
      await supabase
        .from("smartfile_sessions")
        .update({
          extracted_text: data.text,
          extraction_status: "completed",
          content_type: classifyData.contentType,
          structured_records: structureData.records,
          status: "structured",
        })
        .eq("id", session.id);

      toast.dismiss();
      toast.success("Documento reprocessado com sucesso!");
      await loadSession();
    } catch (error: any) {
      console.error('[Retry] ❌ Error:', error);
      toast.dismiss();
      toast.error(`Erro ao reprocessar: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // ✅ Função para monitorar conclusão em BACKGROUND via Realtime
  const setupBackgroundMonitoring = (monitoredSessionId: string, patientId?: string) => {
    // Limpar monitoramento anterior se existir
    if (backgroundMonitorRef.current) {
      supabase.removeChannel(backgroundMonitorRef.current);
    }

    console.log('[setupBackgroundMonitoring] 🔔 Configurando monitoramento em background para sessão:', monitoredSessionId);

    // Configurar Realtime para detectar conclusão
    const channel = supabase
      .channel(`smartfile-background-${monitoredSessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'smartfile_sessions',
          filter: `id=eq.${monitoredSessionId}`,
        },
        async (payload) => {
          const updatedSession = payload.new as any;
          const hasResults = (updatedSession.results_generated > 0) ||
            (Array.isArray(updatedSession.lab_results_ids) && updatedSession.lab_results_ids.length > 0);

          if (hasResults && updatedSession.status === 'completed') {
            console.log('[setupBackgroundMonitoring] ✅ Processamento concluído em background!');

            // Notificar o usuário
            const resultsCount = updatedSession.results_generated || updatedSession.lab_results_ids?.length || 0;
            toast.success(
              `✅ Processamento concluído! ${resultsCount} resultado(s) gerado(s)`,
              {
                description: patientId
                  ? `Ver resultados em /app/pacientes/${patientId}`
                  : 'Os resultados foram salvos com sucesso',
                duration: 10000, // 10 segundos para dar tempo de ler
              }
            );

            // Limpar monitoramento
            supabase.removeChannel(channel);
            backgroundMonitorRef.current = null;
          }
        }
      )
      .subscribe();

    backgroundMonitorRef.current = channel;

    // Cleanup ao desmontar componente
    return () => {
      if (backgroundMonitorRef.current) {
        supabase.removeChannel(backgroundMonitorRef.current);
        backgroundMonitorRef.current = null;
      }
    };
  };


  const handleFinalize = async () => {
    if (finalizing) return; // Prevenir múltiplos cliques

    try {
      setFinalizing(true);
      console.log(`[handleFinalize] START - sessionId: ${sessionId}, patientId: ${patientInfo?.id}, isOnboarding: ${isOnboarding}`);

      // ✅ ONBOARDING: Criar paciente automaticamente (sempre novo no onboarding)
      if (isOnboarding) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast.error("Usuário não autenticado");
          setFinalizing(false);
          return;
        }

        // ✅ ONBOARDING: OBRIGAR preenchimento dos campos editáveis (sem fallbacks)
        // No onboarding, o usuário DEVE preencher explicitamente nome e CPF
        const patientName = editablePatientName.trim();
        const patientCPF = editablePatientCPF.trim();

        // Validar que o nome foi preenchido explicitamente
        if (!patientName || patientName.length === 0) {
          toast.error("⚠️ Por favor, preencha o campo NOME do paciente antes de finalizar");
          setFinalizing(false);
          return;
        }

        // Validar que o CPF foi preenchido explicitamente
        if (!patientCPF || patientCPF.length === 0) {
          toast.error("⚠️ Por favor, preencha o campo CPF do paciente antes de finalizar");
          setFinalizing(false);
          return;
        }

        const cleanCpf = patientCPF.replace(/\D/g, "");

        // Validar que o CPF tem 11 dígitos
        if (cleanCpf.length !== 11) {
          toast.error("⚠️ O campo CPF deve conter 11 dígitos. Verifique e tente novamente.");
          setFinalizing(false);
          return;
        }

        // Validar formato do CPF
        if (!validateCPF(cleanCpf)) {
          toast.error("⚠️ O CPF informado é inválido. Verifique o campo CPF e tente novamente.");
          setFinalizing(false);
          return;
        }

        // ✅ CRÍTICO: No onboarding, sempre criar novo paciente e vincular ANTES de atualizar structured_records
        console.log(`[handleFinalize] 🆕 Criando paciente no onboarding:`, { patientName, patientCPF: cleanCpf });

        const insertData: any = {
          full_name: patientName,
          cpf: cleanCpf,
          owner_id: user.id,
          status: "active", // Sempre active no onboarding pois CPF é obrigatório
        };
        
        console.log(`[handleFinalize] 📝 Criando paciente - CPF: SIM, Status: active`);
        
        const { data: newPatient, error: createError } = await supabase
          .from("patients_onboarding")
          .insert(insertData)
          .select()
          .single();

        if (createError) {
          console.error("[handleFinalize] ❌ Erro ao criar paciente:", createError);
          toast.error("Erro ao criar paciente: " + createError.message);
          setFinalizing(false);
          return;
        }

        console.log(`[handleFinalize] ✅ Paciente criado:`, newPatient.id);
        setPatientInfo(newPatient);
        
        // ✅ CRÍTICO: Vincular paciente à sessão ANTES de atualizar structured_records
        // Isso garante que o trigger SQL execute corretamente
        console.log(`[handleFinalize] 🔗 Vinculando paciente à sessão...`);
        await linkPatientToSmartfileSession(sessionId, newPatient.id);
        console.log(`[handleFinalize] ✅ Paciente vinculado à sessão!`);
        
        // Recarregar sessão para garantir que patient_id está atualizado
        const updatedSession = await getSmartfileSession(sessionId);
        setSession(updatedSession);
      }

      // ✅ VALIDAÇÃO: Verificar se há paciente vinculado antes de finalizar (apenas fora do onboarding)
      if (!isOnboarding && !session?.patient_id && !patientInfo?.id) {
        toast.error("É necessário vincular um paciente antes de finalizar a sessão");
        setFinalizing(false);
        return;
      }

      // ✅ PROCESSAMENTO EM BACKGROUND: Não bloquear o usuário
      const hasRecords = session?.structured_records && Array.isArray(session.structured_records) && session.structured_records.length > 0;
      const isGenerating = generationInProgressRef.current || generating;

      if (hasRecords && isGenerating) {
        console.log('[handleFinalize] ⚡ Processamento continuará em background');
        // Configurar monitoramento em background via Realtime
        const currentPatientId = session?.patient_id || patientInfo?.id;
        setupBackgroundMonitoring(sessionId, currentPatientId);
        toast.info("Processando resultados em background. Você será notificado quando concluir.");
      }

      // ✅ PASSO 0: Vincular patient_id à sessão ANTES de salvar structured_records
      // Isso garante que o trigger SQL execute corretamente
      const currentPatientId = session?.patient_id || patientInfo?.id;
      if (currentPatientId && !session?.patient_id) {
        console.log(`[handleFinalize] 🔗 Vinculando patient_id à sessão...`);
        await linkPatientToSmartfileSession(sessionId, currentPatientId);
        console.log(`[handleFinalize] ✅ Patient vinculado à sessão!`);
        
        // Recarregar sessão após vincular
        const updatedSession = await getSmartfileSession(sessionId);
        setSession(updatedSession);
      }

      // ✅ CRÍTICO: Garantir que temos a sessão mais recente com patient_id vinculado
      // No onboarding, acabamos de criar e vincular o paciente, então precisamos recarregar a sessão
      let currentSession = session;
      if (isOnboarding || !session?.patient_id) {
        currentSession = await getSmartfileSession(sessionId);
        setSession(currentSession);
      }

      // Salvar registros editados antes de finalizar
      // Agora o patient_id já está vinculado, então o trigger SQL vai executar automaticamente
      // O trigger SQL já faz o matching de subcategorias usando slug, então não precisamos chamar syncLabResultsFromSession manualmente
      const updateResult = await supabase
        .from('smartfile_sessions')
        .update({ 
          structured_records: editableRecords,
          updated_at: new Date().toISOString() // Forçar atualização de timestamp para garantir que o trigger detecte a mudança
        })
        .eq('id', sessionId);

      // Salvar notas antes de finalizar
      if (professionalNotes.trim()) {
        await updateSmartfileProfessionalNotes(sessionId, professionalNotes);
      }

      // Obter patientId atualizado (pode ter sido criado no onboarding)
      const finalPatientId = currentSession?.patient_id || patientInfo?.id;
      
      if (finalPatientId && editableRecords.length > 0) {

        try {
          const { saveStructuredRecords } = await import('@/services/miafile.records');

          // ✅ PASSO 1: Salvar lab_results reais (tipo 'lab_result' ou 'results')
          // Esta função apenas registra logs
          await saveStructuredRecords(
            finalPatientId,
            sessionId,
            editableRecords
          );

          // ✅ PASSO 2: Verificar se o trigger SQL disparou e sincronizou os resultados
          // O trigger SQL só dispara quando structured_records muda (IS DISTINCT FROM)
          // Se não mudou, precisamos chamar manualmente ou forçar uma atualização
          const contentType = session?.content_type || 'other';
          const typesProcessedByTrigger = ['lab_result', 'results', 'exam', 'anthropometry', 'body_composition', 'report', 'vitals'];

          // ✅ Verificar se há registros physiotherapy_evolution com structure_type = 'antropometry'
          // Esses também são processados pelo trigger SQL (mesmo que contentType seja 'physiotherapy_evolution')
          const hasAnthropometryInPhysio = editableRecords.some(
            (r: any) => r.type === 'physiotherapy_evolution' && r.structure_type === 'antropometry' && r.is_main_record !== true
          );

          // ✅ Verificar se há registros anamnesis com structure_type = 'antropometry'
          // Esses também são processados pelo trigger SQL (mesmo que contentType seja 'anamnesis')
          const hasAnthropometryInAnamnesis = editableRecords.some(
            (r: any) => r.type === 'anamnesis' && r.structure_type === 'antropometry' && r.is_main_record !== true
          );

          // ✅ Verificar se há registros medical_report com structure_type = 'metrics'
          // Esses também são processados pelo trigger SQL (mesmo que contentType seja 'report')
          const hasMedicalReportMetrics = editableRecords.some(
            (r: any) => r.type === 'medical_report' && r.structure_type === 'metrics' && r.is_main_record !== true
          );

          // ✅ Verificar se há registros biopsy_report com structure_type = 'metrics'
          // Esses também são processados pelo trigger SQL
          const hasBiopsyReportMetrics = editableRecords.some(
            (r: any) => r.type === 'biopsy_report' && r.structure_type === 'metrics' && r.is_main_record !== true
          );

          // ✅ Verificar se há registros vital_sign
          const hasVitalSignRecords = editableRecords.some(
            (r: any) => r.type === 'vital_sign'
          );

          // Se contentType é physiotherapy_evolution, anamnesis ou report mas tem registros processáveis, considerar como processado pelo trigger
          const isProcessedByTrigger = typesProcessedByTrigger.includes(contentType) ||
            (contentType === 'physiotherapy_evolution' && hasAnthropometryInPhysio) ||
            (contentType === 'anamnesis' && hasAnthropometryInAnamnesis) ||
            (contentType === 'report' && (hasMedicalReportMetrics || hasBiopsyReportMetrics)) ||
            hasVitalSignRecords;

          if (isProcessedByTrigger) {
            if (hasAnthropometryInPhysio) {
              console.log(`[handleFinalize] 📊 Detectado physiotherapy_evolution com structure_type='antropometry' - processado pelo trigger SQL`);
            }
            if (hasAnthropometryInAnamnesis) {
              console.log(`[handleFinalize] 📊 Detectado anamnesis com structure_type='antropometry' - processado pelo trigger SQL`);
            }
            if (hasMedicalReportMetrics) {
              console.log(`[handleFinalize] 📋 Detectado medical_report com structure_type='metrics' - processado pelo trigger SQL`);
            }
            if (hasBiopsyReportMetrics) {
              console.log(`[handleFinalize] 🔬 Detectado biopsy_report com structure_type='metrics' - processado pelo trigger SQL`);
            }
            if (hasVitalSignRecords) {
              console.log(`[handleFinalize] ❤️ Detectado vital_sign - processado pelo trigger SQL`);
            }

            // Verificar se já existem resultados salvos para esta sessão
            const { data: existingResults, error: checkError } = await supabase
              .from('lab_results_onboarding')
              .select('id')
              .eq('source_session_id', sessionId)
              .limit(1);

            if (checkError) {
              console.error('[handleFinalize] ❌ Erro ao verificar resultados existentes:', checkError);
            }

            // Se não há resultados e o tipo é processado pelo trigger, forçar sincronização
            if (!existingResults || existingResults.length === 0) {
              console.log(`[handleFinalize] ⚠️ Nenhum resultado encontrado - forçando sincronização manual...`);

              // Forçar atualização do structured_records para disparar o trigger
              // Usar JSONB para garantir que o PostgreSQL detecte como mudança mesmo se o conteúdo for igual
              await supabase
                .from('smartfile_sessions')
                .update({
                  structured_records: editableRecords,
                  updated_at: new Date().toISOString() // Forçar atualização de timestamp
                })
                .eq('id', sessionId);

              // Aguardar um pouco para o trigger executar
              await new Promise(resolve => setTimeout(resolve, 500));

              // Verificar novamente se os resultados foram criados
              const { data: resultsAfterTrigger, error: checkError2 } = await supabase
                .from('lab_results_onboarding')
                .select('id')
                .eq('source_session_id', sessionId)
                .limit(1);

              if (!resultsAfterTrigger || resultsAfterTrigger.length === 0) {
                // Se ainda não há resultados, chamar manualmente a função de sincronização
                console.log(`[handleFinalize] ⚠️ Trigger não disparou - chamando syncLabResultsFromSession manualmente...`);
                const { syncLabResultsFromSession } = await import('@/lib/lab-results-sync');
                await syncLabResultsFromSession(sessionId, finalPatientId);
                console.log(`[handleFinalize] ✅ Sincronização manual concluída!`);
              } else {
                console.log(`[handleFinalize] ✅ Trigger disparou e resultados foram criados!`);
              }
            } else {
              console.log(`[handleFinalize] ✅ Resultados já existem - trigger SQL funcionou corretamente!`);
            }
          } else {
            console.log(`[handleFinalize] ✅ Tipo ${contentType} não processado pelo trigger SQL - usando inserção manual`);

            // ✅ PASSO 3: Extrair e salvar métricas de outros tipos (treino, fisioterapia, etc.)
            // Essas métricas também precisam estar em lab_results_onboarding para gráficos
            const metrics = extractMetricsFromRecords(editableRecords, contentType);
            if (metrics.length > 0) {
              console.log(`[handleFinalize] 📊 Extraindo ${metrics.length} métricas para gráficos...`);

              // Helper function to check if a value is numeric
              const isNumericValue = (value: any): boolean => {
                if (typeof value === 'number') {
                  return !isNaN(value) && isFinite(value);
                }
                if (typeof value === 'string') {
                  return /^-?[0-9]+\.?[0-9]*$/.test(value.trim());
                }
                return false;
              };

              // Helper function to parse numeric value safely
              const parseNumericValue = (value: any): number | null => {
                if (typeof value === 'number') {
                  return isNaN(value) || !isFinite(value) ? null : value;
                }
                if (typeof value === 'string') {
                  const trimmed = value.trim();
                  if (/^-?[0-9]+\.?[0-9]*$/.test(trimmed)) {
                    const parsed = parseFloat(trimmed);
                    return isNaN(parsed) ? null : parsed;
                  }
                }
                return null;
              };

              const insertData = metrics.map(metric => {
                const rawValue = metric.value;
                let numericValue: number | null = null;
                let textValue: string | null = null;

                if (isNumericValue(rawValue)) {
                  numericValue = parseNumericValue(rawValue);
                } else if (rawValue != null && String(rawValue) !== '') {
                  textValue = String(rawValue);
                }

                return {
                  patient_id: finalPatientId,
                  test_name: metric.test_name,
                  value: numericValue,
                  value_text: textValue,
                  unit: metric.unit,
                  collected_at: metric.date,
                  source_type: contentType,
                  source_session_id: sessionId,
                };
              });

              const { error: metricsError } = await supabase
                .from("lab_results_onboarding")
                .insert(insertData);

              if (metricsError) {
                console.error("[handleFinalize] ❌ Erro ao salvar métricas:", metricsError);
              } else {
                console.log(`[handleFinalize] ✅ ${metrics.length} métrica(s) salva(s) em lab_results para gráficos!`);
              }
            }
          }

          toast.success(`✅ ${editableRecords.length} registro(s) salvo(s) no histórico do paciente!`);
        } catch (error) {
          console.error('[handleFinalize] ❌ Erro ao salvar registros:', error);
          toast.error('Erro ao salvar registros: ' + (error as Error).message);
        }
      } else {
        console.warn(`[handleFinalize] ⚠️ Não salvando registros - finalPatientId: ${finalPatientId}, editableRecords: ${editableRecords.length}`);
      }

      // Finalizar sessão após salvar os registros
      await finalizeSmartfileSession(sessionId);
      toast.success("Sessão finalizada com sucesso!");

      // Aguardar para garantir que resultados foram persistidos no banco
      console.log(`[handleFinalize] ⏳ Waiting 1s for DB consistency after finalization...`);
      await new Promise(resolve => setTimeout(resolve, 1000));

      // ENTÃO vincular subcategorias (ORDEM CORRETA: Finalizar → Aguardar → Vincular)
      if (finalPatientId) {
        console.log(`[handleFinalize] 🔗 Linking subcategories after finalization...`);
        console.log(`[handleFinalize] Patient ID: ${finalPatientId}`);

        try {
          // ✅ PASSO 1: Buscar user_id para chamar link_exact_subcategories
          const { data: { user } } = await supabase.auth.getUser();
          if (user) {
            // ✅ PASSO 2: Chamar link_exact_subcategories primeiro (como faz quando cria subcategoria manualmente)
            // Isso garante que todos os resultados sejam vinculados às subcategorias existentes
            const { error: linkExactError } = await supabase.rpc('link_exact_subcategories', {
              p_user_id: user.id,
              p_patient_id: finalPatientId
            });

            if (linkExactError) {
              console.error('[handleFinalize] Error calling link_exact_subcategories:', linkExactError);
            } else {
              console.log('[handleFinalize] ✅ link_exact_subcategories executado com sucesso');
            }
          }

          // ✅ PASSO 3: Também chamar linkByName no modo 'insensitive' para matching mais flexível
          console.log(`[handleFinalize] Mode: insensitive`);
          const result = await linkByName.mutateAsync({
            patientId: finalPatientId,
            mode: 'insensitive'
          });

          if (result.updated > 0) {
            toast.success(
              `✅ ${result.updated} resultados categorizados automaticamente!`,
              { duration: 4000 }
            );
          } else if (result.total > 0) {
            toast.info(
              `ℹ️ ${result.total} resultados categorizados`,
              { duration: 3000 }
            );
          }

          // ✅ PASSO 4: Disparar evento para recarregar gráficos (como faz quando cria subcategoria manualmente)
          window.dispatchEvent(new CustomEvent('reload-health-indicators'));
          console.log('[handleFinalize] ✅ Evento reload-health-indicators disparado');
        } catch (linkError: any) {
          console.error('[handleFinalize] Failed to link subcategories:', linkError);
          toast.warning(
            'Resultados salvos, mas categorização automática falhou. Categorize manualmente.',
            { duration: 5000 }
          );
        }
      } else {
        console.warn('[handleFinalize] ⚠️ No patient linked, skipping subcategory linking');
      }

      // Aguardar onComplete terminar antes de resetar finalizing
      await onComplete();
    } catch (error: any) {
      console.error('[handleFinalize] Error:', error);
      toast.error("Erro ao finalizar sessão");
      // Resetar finalizing apenas em caso de erro
      setFinalizing(false);
      return;
    }
    // Resetar finalizing apenas após onComplete terminar com sucesso
    // Não usar finally para evitar resetar antes de onComplete terminar
    setFinalizing(false);
  };

  // ✅ Função helper para descartar sessão (usada tanto por handleDiscard quanto handleBack)
  const discardSession = async (onSuccess: () => void, showToast: boolean = true) => {
    if (!session) {
      toast.error("Sessão não encontrada");
      return;
    }

    try {
      // 1. Deletar todos os resultados laboratoriais associados à sessão
      const { error: labResultsError } = await supabase
        .from("lab_results_onboarding")
        .delete()
        .eq("source_session_id", session.id);

      if (labResultsError) {
        console.error("Error deleting lab results:", labResultsError);
        // Continuar mesmo com erro - tentar deletar outros dados
      }

      // 2. Se for uma sessão assistida, buscar e atualizar a submissão
      if (fromAssisted) {
        const { data: assistedSubmission, error: submissionError } = await supabase
          .from("assisted_submissions")
          .select("id")
          .eq("smartfile_session_id", session.id)
          .single();

        if (submissionError || !assistedSubmission) {
          console.error("Error finding assisted submission:", submissionError);
          // Continuar mesmo sem encontrar submissão - pode ser que não exista mais
        } else {
          // Atualizar status da submissão para rejected
          const { error: updateError } = await supabase
            .from("assisted_submissions")
            .update({
              status: "rejected",
              processed_at: new Date().toISOString()
            })
            .eq("id", assistedSubmission.id);

          if (updateError) {
            console.error("Error updating submission:", updateError);
            // Continuar mesmo com erro - tentar deletar a sessão
          }
        }
      }

      // 3. Deletar a sessão do smartfile (sempre fazer isso por último)
      const { error: deleteError } = await supabase
        .from("smartfile_sessions")
        .delete()
        .eq("id", session.id);

      if (deleteError) {
        console.error("Error deleting session:", deleteError);
        toast.error("Erro ao deletar sessão");
        return;
      }

      // Mostrar toast apenas se solicitado
      if (showToast) {
        toast.success(fromAssisted ? "Submissão descartada com sucesso" : "Sessão descartada com sucesso");
      }

      // Chamar callback de sucesso
      onSuccess();
    } catch (error: any) {
      console.error("Error discarding:", error);
      toast.error(error.message || "Erro ao descartar");
    }
  };

  const handleDiscard = async () => {
    await discardSession(() => onComplete(), true);
  };

  // ✅ Função para lidar com o botão "Voltar" - descarta a sessão antes de voltar
  const handleBack = async () => {
    // Sempre redirecionar para /app quando clicar em voltar
    await discardSession(() => {
      navigate("/app");
    }, false); // Não mostrar toast ao voltar
  };

  // ✅ REMOVIDO: handleReprocess não é mais necessário
  // Os dados já foram processados por smartfile-v15-process-with-tools
  // Se necessário reprocessar, use o botão de reclassificar no header
  const handleReprocess = async () => {
    toast.info("Use o botão 'Reclassificar' no card de classificação para reprocessar o documento.");
  };

  const handlePatientLinked = async (patientId: string) => {
    try {
      await linkPatientToSmartfileSession(sessionId, patientId);

      // ✅ O trigger SQL vai executar automaticamente quando structured_records for atualizado
      // Não precisamos chamar syncLabResultsFromSession manualmente aqui para evitar duplicação
      // O trigger SQL já faz o matching de subcategorias usando slug quando patient_id está vinculado

      // Recarregar sessão para atualizar patient_id
      await loadSession();

      toast.success("Paciente vinculado com sucesso!");

      // ✅ REMOVIDO: Não gerar resultados automaticamente - usar apenas dados de structured_records
      // Os dados já foram processados por smartfile-v15-process-with-tools
      // Serão salvos quando o usuário finalizar a sessão via saveStructuredRecords
    } catch (error: any) {
      toast.error("Erro ao vincular paciente");
      console.error(error);
    }
  };


  // ✅ REMOVIDO: generateResultsAutomatically não é mais necessário
  // Os dados já foram processados por smartfile-v15-process-with-tools e salvos em structured_records
  // Serão salvos quando o usuário finalizar a sessão via saveStructuredRecords
  const generateResultsAutomatically = async (patientId: string) => {
    console.log('[generateResultsAutomatically] ⚠️ Função desabilitada - usar saveStructuredRecords no handleFinalize');
  };

  // ✅ Função helper para salvar edições automaticamente na sessão
  const handleRecordsChange = (updatedRecords: any[]) => {
    // Atualizar estado local
    setEditableRecords(updatedRecords);

    // Salvar imediatamente na sessão para preservar edições
    supabase
      .from('smartfile_sessions')
      .update({ structured_records: updatedRecords })
      .eq('id', sessionId)
      .then(({ error }) => {
        if (error) {
          console.error('[handleRecordsChange] Erro ao salvar edições:', error);
        } else {
          console.log('[handleRecordsChange] ✅ Edições salvas na sessão');
        }
      });
  };

  // ✅ Função helper para extrair métricas dos registros (para gráficos)
  const extractMetricsFromRecords = (records: any[], contentType: string): Array<{
    test_name: string;
    value: number;
    unit: string;
    date: string;
  }> => {
    const allMetrics: Array<{ test_name: string; value: number; unit: string; date: string }> = [];

    for (const record of records) {
      // Pular registro principal de training_plan
      if (record.type === 'training_plan' && record.is_main_record === true) {
        continue;
      }

      const recordDate = record.event_date || record.date || new Date().toISOString().split('T')[0];

      // ✅ PULAR tipos sincronizados via trigger SQL
      if (
        record.type === 'lab_result' ||
        record.type === 'results' ||
        record.type === 'anthropometry' ||
        record.type === 'vital_sign' ||
        (record.type === 'physiotherapy_evolution' && record.structure_type === 'antropometry') ||
        (record.type === 'anamnesis' && record.structure_type === 'antropometry') ||
        (record.type === 'medical_report' && record.structure_type === 'metrics') ||
        (record.type === 'biopsy_report' && record.structure_type === 'metrics')
      ) {
        continue;
      }

      // Extrair métricas do array metrics (formato novo) - apenas para tipos não-lab
      if (record.metrics && Array.isArray(record.metrics)) {
        record.metrics.forEach((metric: any) => {
          if (metric.name && metric.value != null) {
            allMetrics.push({
              test_name: metric.name,
              value: parseFloat(metric.value),
              unit: metric.unit || '',
              date: recordDate
            });
          }
        });
      }

      // Extrair métricas específicas por tipo (apenas tipos não-lab)
      switch (contentType) {
        // ✅ REMOVIDO: case 'results'/'exam' - já sincronizados via trigger SQL

        case 'training_plan':
          // Extrair carga de exercícios dos training_groups
          if (record.training_groups && Array.isArray(record.training_groups)) {
            record.training_groups.forEach((group: any) => {
              if (group.exercises && Array.isArray(group.exercises)) {
                group.exercises.forEach((exercise: any) => {
                  if (exercise.load != null && exercise.exercise) {
                    allMetrics.push({
                      test_name: `Carga - ${exercise.exercise}`,
                      value: parseFloat(exercise.load),
                      unit: exercise.load_unit || 'kg',
                      date: recordDate
                    });
                  }
                });
              }
            });
          }
          break;

        case 'physiotherapy_evolution':
          // ⚠️ Pular registros com structure_type = 'antropometry' - já processados pelo trigger SQL
          if (record.structure_type === 'antropometry') {
            break; // Pular - sincronização automática via trigger SQL
          }

          // Processar apenas registros normais de fisioterapia (sem structure_type = 'antropometry')
          if (record.load != null) {
            const exerciseLower = (record.exercise || "").toLowerCase();
            const loadUnit = record.load_unit || "kg";
            if (!record.exercise || exerciseLower.includes("peso") || exerciseLower === "peso") {
              allMetrics.push({
                test_name: "Peso",
                value: parseFloat(record.load),
                unit: loadUnit,
                date: recordDate
              });
            } else {
              allMetrics.push({
                test_name: `Carga - ${record.exercise}`,
                value: parseFloat(record.load),
                unit: loadUnit,
                date: recordDate
              });
            }
          }
          if (record.pain_scale_eva != null) {
            allMetrics.push({
              test_name: "Escala de Dor (EVA)",
              value: parseFloat(record.pain_scale_eva),
              unit: "pontos",
              date: recordDate
            });
          }
          break;

        case 'nutritional_assessment':
        case 'body_composition':
          // ⚠️ anthropometry removido: já processado pelo trigger SQL
          if (record.value != null) {
            const labelMap: Record<string, string> = {
              peso: "Peso",
              altura: "Altura",
              imc: "IMC",
              circunferencia_cintura: "Circunferência da Cintura",
              percentual_gordura: "Percentual de Gordura",
              massa_muscular: "Massa Muscular"
            };
            const measurementKey = (record.measurement_type || record.measurement || '').toLowerCase();
            const label = labelMap[measurementKey] || measurementKey.replace(/_/g, ' ');

            allMetrics.push({
              test_name: label,
              value: parseFloat(record.value),
              unit: record.unit || '',
              date: recordDate
            });
          }
          break;

        case 'vitals':
          if (record.value != null && record.vital_type !== 'pressao_arterial') {
            allMetrics.push({
              test_name: record.vital_type?.replace(/_/g, ' ') || 'Sinal Vital',
              value: parseFloat(record.value),
              unit: record.unit || '',
              date: recordDate
            });
          }
          if (record.blood_pressure_systolic) {
            allMetrics.push({
              test_name: "Pressão Arterial Sistólica",
              value: parseFloat(record.blood_pressure_systolic),
              unit: "mmHg",
              date: recordDate
            });
          }
          if (record.blood_pressure_diastolic) {
            allMetrics.push({
              test_name: "Pressão Arterial Diastólica",
              value: parseFloat(record.blood_pressure_diastolic),
              unit: "mmHg",
              date: recordDate
            });
          }
          break;
      }
    }

    return allMetrics;
  };

  // ✅ REMOVIDO: saveMetricsToLabResults não é mais necessário
  // Métricas já estão em structured_records e são sincronizadas automaticamente via trigger SQL
  // Esta função foi removida para evitar duplicação de dados

  // Mapeamento de tipos para labels em português
  const getContentTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'anatomopathology': 'Anatomia Patológica / Histopatologia',
      'biopsy_report': 'Biópsia',
      'exam': 'Exame de Imagem',
      'result': 'Exame Laboratorial',
      'results': 'Exame Laboratorial',
      'report': 'Laudo Médico',
      'prescription': 'Prescrição Médica',
      'anamnesis': 'Anamnese / Consulta',
      'physiotherapy_evolution': 'Evolução Fisioterapêutica',
      'nutritional_assessment': 'Avaliação Nutricional',
      'training_plan': 'Plano de Treino',
      'meal_plan': 'Plano Alimentar',
      'anthropometry': 'Antropometria',
      'psychotherapy_session': 'Sessão Psicoterapia',
      'aesthetic_protocol': 'Protocolo Estético',
      'body_composition': 'Composição Corporal',
      'chiropractic_adjustment': 'Ajuste Quiroprático',
      'vitals': 'Sinais Vitais'
    };
    return labels[type] || type;
  };

  // Ícones específicos por tipo de documento
  const getContentTypeIcon = (type: string) => {
    const iconClass = "h-8 w-8 text-primary";

    const icons: Record<string, JSX.Element> = {
      'anatomopathology': <Microscope className={iconClass} />,
      'exam': <FlaskConical className={iconClass} />,
      'result': <FlaskConical className={iconClass} />,
      'results': <FlaskConical className={iconClass} />,
      'report': <FileText className={iconClass} />,
      'prescription': <Pill className={iconClass} />,
      'anamnesis': <ClipboardList className={iconClass} />,
      'physiotherapy_evolution': <Activity className={iconClass} />,
      'nutritional_assessment': <Apple className={iconClass} />,
      'training_plan': <Dumbbell className={iconClass} />,
      'meal_plan': <UtensilsCrossed className={iconClass} />,
      'psychotherapy_session': <Brain className={iconClass} />,
      'aesthetic_protocol': <Sparkles className={iconClass} />,
      'body_composition': <Scale className={iconClass} />,
      'chiropractic_adjustment': <Stethoscope className={iconClass} />,
      'vitals': <Heart className={iconClass} />
    };

    return icons[type] || <FileText className={iconClass} />;
  };

  // Função para reclassificar documento
  const handleReanalyze = async () => {
    if (!session) return;

    try {
      setGenerating(true);
      toast.loading("Reanalisando documento...");

      // Reprocessar com o tipo atual ou novo tipo selecionado
      const { data, error } = await supabase.functions.invoke('smartfile-v15-process-with-tools', {
        body: {
          extractedText: session.extracted_text,
          fileType: session.file_type,
          sessionId: sessionId,
          contentType: session.content_type
        }
      });

      if (error) throw error;

      if (!data.success) {
        throw new Error(data.error || 'Falha ao reanalisar');
      }

      toast.dismiss();
      toast.success("Documento reanalisado com sucesso!");

      // Recarregar sessão
      await loadSession();
    } catch (error: any) {
      console.error('[handleReanalyze] Error:', error);
      toast.dismiss();
      toast.error(`Erro ao reanalisar: ${error.message}`);
    } finally {
      setGenerating(false);
    }
  };

  // Função para alterar tipo de conteúdo
  const handleContentTypeChange = async (newType: string) => {
    if (!session) return;

    try {
      const { error } = await supabase
        .from('smartfile_sessions')
        .update({ content_type: newType, updated_at: new Date().toISOString() })
        .eq('id', sessionId);

      if (error) throw error;

      toast.success('Tipo de documento atualizado!');

      // Se houver texto extraído, reprocessar com o novo tipo
      if (session.extracted_text) {
        await handleReanalyze();
      } else {
        await loadSession();
      }
    } catch (error: any) {
      console.error('Error updating content type:', error);
      toast.error('Erro ao atualizar tipo');
    }
  };


  if (loading) {
    return (
      <div className="space-y-6 pb-6 lg:pb-24 w-full max-w-full">
        <Card>
          <CardHeader>
            <Skeleton className="h-7 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!session) {
    return (
      <Card className="border-red-500">
        <CardContent className="pt-6">
          <p className="text-red-500">Sessão não encontrada</p>
        </CardContent>
      </Card>
    );
  }

  // Mostrar overlay de processamento quando vier de coleta assistida
  if (fromAssisted && processingStage) {
    return (
      <div className="space-y-6 pb-6 lg:pb-24 w-full max-w-full">
        <Card className="border-primary">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <div>
                  <p className="font-semibold text-primary">{processingStage}</p>
                  <p className="text-sm text-muted-foreground">
                    {processingProgress}% concluído
                  </p>
                </div>
              </div>
              <Progress value={processingProgress} className="w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }


  // Mapeamento de tipos para o dropdown
  const contentTypeOptions = [
    { value: 'results', label: '🧪 Exames Laboratoriais', area: 'Geral' },
    { value: 'exam', label: '📸 Exames de Imagem', area: 'Geral' },
    { value: 'prescription', label: '💊 Prescrição Médica', area: 'Geral' },
    { value: 'report', label: '📋 Laudo Médico', area: 'Geral' },
    { value: 'anamnesis', label: '📝 Anamnese', area: 'Geral' },
    { value: 'physiotherapy_evolution', label: '💪 Evolução Fisioterápica', area: 'Fisioterapia' },
    { value: 'nutritional_assessment', label: '🍎 Avaliação Nutricional', area: 'Nutrição' },
    { value: 'meal_plan', label: '🥗 Plano Alimentar', area: 'Nutrição' },
    { value: 'training_plan', label: '🏋️ Plano de Treino', area: 'Educação Física' },
    { value: 'psychotherapy_session', label: '🧠 Sessão de Psicoterapia', area: 'Psicologia' },
    { value: 'chiropractic_adjustment', label: '🦴 Ajuste Quiroprático', area: 'Quiropraxia' },
    { value: 'aesthetic_protocol', label: '✨ Protocolo Estético', area: 'Estética' },
    { value: 'vitals', label: '❤️ Sinais Vitais', area: 'Transversal' },
    { value: 'body_composition', label: '📊 Composição Corporal', area: 'Transversal' },
    { value: 'anthropometry', label: '📏 Antropometria', area: 'Transversal' },
    { value: 'other', label: '📄 Outros', area: 'Geral' },
  ];

  return (
    <div className="space-y-6 pb-6 lg:pb-24 w-full max-w-full">
      {/* Alerta se sessão antiga */}
      {(session as any).created_at && Date.now() - new Date((session as any).created_at).getTime() > 3600000 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Esta sessão foi criada há mais de 1 hora. Se você fez upload recente,
            verifique se está visualizando a sessão correta.
          </AlertDescription>
        </Alert>
      )}

      {/* Alerta se extração falhou - alinhado com backend (mínimo 10 chars) */}
      {session &&
        !fromAssisted &&
        session.extraction_status !== 'completed' &&
        (!session.extracted_text || session.extracted_text.trim().length < 10) && (
          <Card className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
            <CardContent className="pt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">
                    Extração Incompleta
                  </h4>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    O texto do documento não foi extraído corretamente. Clique para tentar novamente.
                  </p>
                </div>
                <Button
                  onClick={retryExtraction}
                  variant="outline"
                  className="border-yellow-500 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
                  disabled={generating}
                >
                  {generating ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-4 w-4" />
                  )}
                  Reprocessar
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      {/* Informações do Paciente */}
      <div className="space-y-4 lg:space-y-6">
        {patientInfo && !isOnboarding ? (
          <Card className="border-secondary">
            <CardHeader className="px-4 sm:px-6">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-primary">
                    Informações do Paciente
                  </CardTitle>
                  <CardDescription>Paciente vinculado</CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPatientInfo(null);
                    setExtractedPatientInfo(null);
                  }}
                  className="border-secondary text-secondary hover:bg-secondary/10"
                >
                  <User className="h-4 w-4 mr-2" />
                  Trocar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Nome</p>
                  <p className="text-sm font-medium text-primary">{patientInfo.full_name}</p>
                </div>

                {patientInfo.cpf && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">CPF</p>
                    <p className="text-sm">{patientInfo.cpf}</p>
                  </div>
                )}

                {patientInfo.data_nascimento && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">
                      Data de Nascimento
                    </p>
                    <p className="text-sm">
                      {new Date(patientInfo.data_nascimento).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                )}

                {patientInfo.sexo && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Gênero</p>
                    <p className="text-sm capitalize">{patientInfo.sexo}</p>
                  </div>
                )}

                {patientInfo.celular && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Telefone</p>
                    <p className="text-sm">{patientInfo.celular}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ) : isOnboarding ? (
          // ✅ ONBOARDING: Mostrar campos editáveis para nome e CPF (mesmo quando paciente já vinculado)
          <Card className="border-secondary">
            <CardHeader className="px-4 sm:px-6">
              <CardTitle className="text-primary">
                Informações do Paciente
              </CardTitle>
              <CardDescription>
                {patientInfo 
                  ? "Você pode editar os dados antes de finalizar. Nome e CPF são obrigatórios."
                  : "Preencha o nome e CPF do paciente. Estes campos são obrigatórios para finalizar o onboarding. O paciente será criado automaticamente ao finalizar."
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 sm:px-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="onboarding-patient-name">Nome *</Label>
                  <Input
                    id="onboarding-patient-name"
                    value={editablePatientName}
                    onChange={(e) => setEditablePatientName(e.target.value)}
                    placeholder="Nome do paciente"
                    className="w-full"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="onboarding-patient-cpf">CPF *</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setEditablePatientCPF(generateValidCPF())}
                      className="text-primary border-primary hover:bg-primary/10 h-6 text-xs px-2"
                    >
                      Gerar CPF aleatório para teste
                    </Button>
                  </div>
                  <Input
                    id="onboarding-patient-cpf"
                    value={editablePatientCPF || ""}
                    onChange={(e) => setEditablePatientCPF(e.target.value.replace(/\D/g, ""))}
                    placeholder="CPF (somente números)"
                    maxLength={11}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <PatientLinker
            suggestedName={extractedPatientInfo?.name || session?.temp_patient_data?.name}
            suggestedCPF={extractedPatientInfo?.cpf || session?.temp_patient_data?.cpf}
            onPatientLinked={handlePatientLinked}
            isChangingPatient={false}
          />
        )}

        {/* Conteúdo Extraído */}
        {session.extracted_text && (
          <ExtractedContentEditor
            sessionId={sessionId}
            extractedText={session.extracted_text}
            contentType={session.content_type || 'other'}
            contentTypeLabel={session.content_type ? getContentTypeLabel(session.content_type) : undefined}
            onContentUpdated={async (newText) => {
              if (session) {
                setSession({ ...session, extracted_text: newText });
              }
              await loadSession();
            }}
            onRegenerate={async () => {
              // ✅ Usar sempre smartfile-v15-process-with-tools (mesma função usada no Step1)
              setEditableRecords([]);

              const { data, error } = await supabase.functions.invoke('smartfile-v15-process-with-tools', {
                body: {
                  extractedText: session.extracted_text,
                  fileType: session.file_type,
                  sessionId: sessionId
                }
              });

              if (error) throw error;

              if (!data.success) {
                throw new Error(data.error || 'Falha ao reprocessar');
              }

              // Os dados já foram salvos pela função smartfile-v15-process-with-tools
              // Apenas recarregar a sessão
              await handleGenerateInsights();
              await loadSession();
            }}
          />
        )}
      </div>

      {/* Registros Estruturados */}
      {session.structured_records && editableRecords.length > 0 && (
        <div className="space-y-4 lg:space-y-6">
          {/* Renderizar componente específico por tipo */}
          {session.content_type === 'nutritional_assessment' && (
            <NutritionalAssessmentCard
              records={editableRecords}
              onRecordsChange={handleRecordsChange}
            />
          )}

          {session.content_type === 'meal_plan' && (() => {
            // Verificar se há meal_plan com structure_type: "food"
            const hasFoodType = editableRecords.some((r: any) =>
              r.type === 'meal_plan' &&
              r.structure_type === 'food' &&
              !(r.is_main_record === true || r.is_main_record === 'true')
            );

            // Verificar se há meal_plan com structure_type: "weekly"
            const hasWeeklyType = editableRecords.some((r: any) =>
              r.type === 'meal_plan' &&
              r.structure_type === 'weekly' &&
              !(r.is_main_record === true || r.is_main_record === 'true')
            );

            // Criar uma sessão mock para os componentes
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            // Renderizar componente apropriado
            if (hasFoodType) {
              return (
                <SmartfileMealPlanFoodDetails
                  session={mockSession}
                  onRefresh={handleRefresh}
                  onRecordsChange={handleRecordsChange}
                />
              );
            } else if (hasWeeklyType) {
              return (
                <SmartfileMealPlanDetails
                  session={mockSession}
                  onRefresh={handleRefresh}
                  onRecordsChange={handleRecordsChange}
                />
              );
            }

            // Fallback: não renderizar nada se não houver tipos válidos
            return null;
          })()}

          {session.content_type === 'training_plan' && (() => {
            // Criar uma sessão mock para o componente
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            return (
              <SmartfileTrainingPlanDetails
                session={mockSession}
                onRefresh={handleRefresh}
                onRecordsChange={handleRecordsChange}
              />
            );
          })()}

          {session.content_type === 'body_composition' && (
            <BodyCompositionCard
              records={editableRecords}
              onRecordsChange={handleRecordsChange}
            />
          )}

          {session.content_type === 'psychotherapy_session' && (
            <PsychotherapySessionCard
              records={editableRecords}
              onRecordsChange={handleRecordsChange}
            />
          )}

          {session.content_type === 'chiropractic_adjustment' && (
            <ChiropracticAdjustmentCard
              records={editableRecords}
              onRecordsChange={handleRecordsChange}
            />
          )}

          {session.content_type === 'aesthetic_protocol' && (
            <AestheticProtocolCard
              records={editableRecords}
              onRecordsChange={handleRecordsChange}
            />
          )}

          {session.content_type === 'vitals' && (() => {
            // Criar uma sessão mock para o componente
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            return (
              <SmartfileVitalsDetails
                session={mockSession}
                onRefresh={handleRefresh}
                onRecordsChange={handleRecordsChange}
              />
            );
          })()}

          {session.content_type === 'physiotherapy_evolution' && (() => {
            // Criar uma sessão mock para o componente
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            return (
              <SmartfilePhysiotherapyEvolutionDetails
                session={mockSession}
                onRefresh={handleRefresh}
                onRecordsChange={handleRecordsChange}
              />
            );
          })()}

          {session.content_type === 'anthropometry' && (() => {
            // Criar uma sessão mock para o componente
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            return (
              <SmartfileAnthropometryDetails
                session={mockSession}
                onRefresh={handleRefresh}
                onRecordsChange={handleRecordsChange}
              />
            );
          })()}

          {session.content_type === 'anamnesis' && (() => {
            // Criar uma sessão mock para o componente
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            return (
              <SmartfileAnamnesisDetails
                session={mockSession}
                onRefresh={handleRefresh}
                onRecordsChange={handleRecordsChange}
              />
            );
          })()}

          {/* Verificar biópsia primeiro (tem prioridade sobre medical_report) */}
          {(() => {
            // Verificar se há registros de biopsy_report
            const hasBiopsyReportRecords = editableRecords.some((r: any) => r.type === 'biopsy_report');

            if (!hasBiopsyReportRecords) {
              return null;
            }

            // Criar uma sessão mock para o componente
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            return (
              <SmartfileBiopsyDetails
                session={mockSession}
                onRefresh={handleRefresh}
                onRecordsChange={handleRecordsChange}
              />
            );
          })()}

          {session.content_type === 'report' && (() => {
            // Verificar se há registros de medical_report (mas não biopsy_report)
            const hasMedicalReportRecords = editableRecords.some((r: any) => r.type === 'medical_report');
            const hasBiopsyReportRecords = editableRecords.some((r: any) => r.type === 'biopsy_report');

            // Se já renderizou biópsia, não renderizar medical_report
            if (hasBiopsyReportRecords || !hasMedicalReportRecords) {
              return null;
            }

            // Criar uma sessão mock para o componente
            const mockSession = {
              id: sessionId,
              file_name: session.file_name || '',
              content_type: session.content_type,
              ai_summary: session.ai_summary,
              created_at: (session as any).created_at || new Date().toISOString(),
              structured_records: editableRecords,
              patient_id: session.patient_id,
              status: session.status
            };

            // Função de refresh que recarrega os dados
            const handleRefresh = async () => {
              await loadSession();
            };

            return (
              <SmartfileMedicalReportDetails
                session={mockSession}
                onRefresh={handleRefresh}
                onRecordsChange={handleRecordsChange}
              />
            );
          })()}

          {/* Fallback para tipos antigos ou genéricos - lab_results */}
          {(!session.content_type || ['results', 'exam', 'prescription', 'report', 'anatomopathology', 'other', 'body_composition'].includes(session.content_type)) && (() => {
            // Verificar se há lab_results
            const hasLabResults = editableRecords.some((r: any) => r.type === 'lab_result');
            // Verificar se há medical_report (usa componente específico SmartfileMedicalReportDetails)
            const hasMedicalReportRecords = editableRecords.some((r: any) => r.type === 'medical_report');
            // Verificar se há biopsy_report (usa componente específico SmartfileBiopsyDetails)
            const hasBiopsyReportRecords = editableRecords.some((r: any) => r.type === 'biopsy_report');

            // Se for report com medical_report ou biopsy_report records, usa o componente específico
            if (session.content_type === 'report' && (hasMedicalReportRecords || hasBiopsyReportRecords)) {
              return null;
            }

            if (hasLabResults) {
              // Criar uma sessão mock para o componente
              const mockSession = {
                id: sessionId,
                file_name: session.file_name || '',
                content_type: session.content_type,
                ai_summary: session.ai_summary,
                created_at: (session as any).created_at || new Date().toISOString(),
                structured_records: editableRecords,
                patient_id: session.patient_id,
                status: session.status
              };

              // Função de refresh que recarrega os dados
              const handleRefresh = async () => {
                await loadSession();
              };

              return (
                <SmartfileSessionDetails
                  session={mockSession}
                  onRefresh={handleRefresh}
                  onRecordsChange={handleRecordsChange}
                />
              );
            }

            // Fallback para outros tipos (usar componentes antigos)
            return (
              <>
                <GroupedRecordsView
                  records={editableRecords}
                  onRecordsChange={handleRecordsChange}
                />

                {!['prescription'].includes(session.content_type || '') && (
                  <GroupedResultsView
                    records={editableRecords}
                    onRecordsChange={handleRecordsChange}
                    contentType={session.content_type}
                  />
                )}
              </>
            );
          })()}
        </div>
      )}

      {/* Categorização e Gráficos */}
      {session.structured_records && editableRecords.length > 0 && session.patient_id && (
        <CategorizationSuggestions
          records={editableRecords}
          patientId={session.patient_id}
          sessionId={sessionId}
          onSubcategoriesCreated={loadSession}
        />
      )}

      {/* Alerta para análise incompleta em coleta assistida - apenas se dados estruturais faltarem */}
      {(() => {
        const hasStructuredCore =
          !!session.content_type &&
          !!session.structured_records &&
          Array.isArray(session.structured_records) &&
          session.structured_records.length > 0;

        const shouldShowAssistedIncompleteAlert =
          fromAssisted &&
          !hasStructuredCore &&
          session.status !== 'failed';

        return shouldShowAssistedIncompleteAlert && (
          <Alert className="border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="font-medium text-yellow-800 dark:text-yellow-200">
                  Análise incompleta detectada
                </p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                  Alguns dados não foram processados completamente. Tente reprocessar a análise.
                </p>
              </div>
              <Button
                onClick={handleReprocess}
                size="sm"
                variant="outline"
                className="border-yellow-500 text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reprocessar
              </Button>
            </AlertDescription>
          </Alert>
        );
      })()}

      {/* Loading state para insights em coleta assistida */}
      {fromAssisted && !session.ai_insights && session.status !== 'failed' && session.extracted_text && !isAIDisabled() && (
        <Card className="border-primary/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-primary">
              <Loader2 className="h-5 w-5 animate-spin" />
              Insights Clínicos
            </CardTitle>
            <CardDescription>Gerando análise automática...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-3 text-muted-foreground">
              <div className="space-y-2 w-full">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-5/6" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      {session.ai_insights && !isAIDisabled() && (
        <div className="space-y-4 lg:space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-primary">Insights Clínicos</CardTitle>
              <CardDescription>Análise gerada automaticamente pela IA</CardDescription>
            </CardHeader>
          </Card>

          <div className="space-y-3">
            {Array.isArray(session.ai_insights) ? (
              session.ai_insights.map((insight: any, idx: number) => (
                <Card
                  key={idx}
                  className="border-l-4 border-l-secondary"
                >
                  <CardContent className="px-4 sm:px-6 py-4">
                    <p className="font-medium text-sm">
                      {insight.title || insight.message}
                    </p>
                    {insight.message && insight.title && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {insight.message}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="border-l-4 border-l-secondary">
                <CardContent className="px-4 sm:px-6 py-4">
                  <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-[200px]">
                    {JSON.stringify(session.ai_insights, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Resumo Final da IA */}
      {session.ai_summary && !isAIDisabled() && (
        <Card className="border-secondary">
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="flex items-center gap-2 text-secondary">
              <Sparkles className="h-5 w-5" />
              Resumo Final da IA
            </CardTitle>
            <CardDescription>
              Análise completa do conteúdo extraído do documento
            </CardDescription>
          </CardHeader>
          <CardContent className="px-4 sm:px-6">
            <div className="prose prose-sm max-w-none">
              {session.ai_summary.split("\n\n").map((paragraph, idx) => (
                <p key={idx} className="text-sm mb-3 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Análise Profissional */}
      <Card>
        <CardHeader>
          <CardTitle className="text-primary">Análise Profissional</CardTitle>
          <CardDescription>
            Suas observações serão salvas automaticamente ao finalizar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={professionalNotes}
            onChange={(e) => setProfessionalNotes(e.target.value)}
            placeholder="Adicione suas observações profissionais sobre este documento..."
            rows={10}
            className="min-h-[300px] resize-none"
          />
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="space-y-4 pt-4 lg:pt-6 mt-4 lg:mt-6 border-t">
        {!isOnboarding && !session?.patient_id && (
          <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              É necessário vincular um paciente antes de finalizar a sessão. Use o card "Vincular Paciente" acima para vincular ou criar um paciente.
            </AlertDescription>
          </Alert>
        )}
        <div className={`flex items-center gap-3 justify-between`}>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={linkByName.isPending || finalizing}
              className="w-full sm:w-auto"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Voltar
            </Button>
            {!hideDiscard && (
              <Button
                variant="destructive"
                onClick={handleDiscard}
                disabled={linkByName.isPending || finalizing}
                className="w-full sm:w-auto"
              >
                <X className="h-4 w-4 mr-2" />
                Descartar
              </Button>
            )}
          </div>

          <Button
            onClick={handleFinalize}
            className="bg-secondary hover:bg-secondary/90 w-full sm:w-auto"
            disabled={linkByName.isPending || (!isOnboarding && !session?.patient_id) || finalizing}
            title={!isOnboarding && !session?.patient_id ? "É necessário vincular um paciente antes de finalizar" : finalizing ? "Processando..." : undefined}
          >
            {finalizing || linkByName.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Finalizando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {fromAssisted ? "Aprovar e Finalizar" : "Finalizar"}
              </>
            )}
          </Button>
        </div>
      </div>

    </div>
  );
}
